import Keycloak, { type KeycloakLoginOptions, type KeycloakLogoutOptions } from 'keycloak-js';

import { KEYCLOAK_CONFIG } from './utils/constants';
import { getCurrentUrlWithoutExternalPlanQueryParams, storeExternalPlanQueryParamsFromSearch } from './utils/externalPlanDataStorage';
import { hasRequiredFloorplannerModule } from './utils/keycloakUserUtils';

const keycloak = new Keycloak(KEYCLOAK_CONFIG);
export const KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS = 30;

let initPromise: Promise<boolean> | null = null;
let refreshPromise: Promise<string | undefined> | null = null;

export const initKeycloak = (): Promise<boolean> => {
  if (initPromise) {
    return initPromise;
  }

  if (keycloak.didInitialize) {
    return Promise.resolve(Boolean(keycloak.authenticated));
  }

  storeExternalPlanQueryParamsFromSearch();
  const redirectUri = getCurrentUrlWithoutExternalPlanQueryParams();

  initPromise = keycloak
    .init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      flow: 'standard',
      pkceMethod: 'S256',
      ...(redirectUri ? { redirectUri } : {}),
    })
    .catch((error) => {
      initPromise = null;
      throw error;
    });

  return initPromise;
};

const waitForRedirect = async (): Promise<void> => new Promise(() => {});

export const loginKeycloak = async (options: KeycloakLoginOptions = {}): Promise<void> => {
  try {
    await keycloak.login(options);
  } catch (error) {
    console.warn('Keycloak adapter login failed, trying direct redirect:', error);
    const loginUrl = await keycloak.createLoginUrl(options);
    window.location.href = loginUrl;
    await waitForRedirect();
  }
};

export const logoutKeycloak = async (options: KeycloakLogoutOptions = {}): Promise<void> => {
  try {
    await keycloak.logout(options);
  } catch (error) {
    console.warn('Keycloak adapter logout failed, trying direct redirect:', error);
    window.location.href = keycloak.createLogoutUrl(options);
    await waitForRedirect();
  }
};

export const refreshKeycloakAccessToken = async (minValidity = KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS): Promise<string | undefined> => {
  if (!keycloak.authenticated) {
    return undefined;
  }

  if (!refreshPromise) {
    refreshPromise = keycloak
      .updateToken(minValidity)
      .then(() => keycloak.token)
      .catch((error) => {
        keycloak.clearToken();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const getKeycloakAccessToken = (): string | undefined => keycloak.token;

export const getValidKeycloakAccessToken = async (minValidity = KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS): Promise<string | undefined> => {
  try {
    await initKeycloak();
  } catch {
    return undefined;
  }

  if (!keycloak.authenticated) {
    return undefined;
  }

  try {
    return await refreshKeycloakAccessToken(minValidity);
  } catch {
    return undefined;
  }
};

export const getValidKeycloakAccessTokenOrFallback = async (
  fallbackToken?: string | null,
  minValidity = KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS,
): Promise<string | undefined> => {
  const accessToken = await getValidKeycloakAccessToken(minValidity);
  if (accessToken) {
    return accessToken;
  }

  if (!fallbackToken || keycloak.authenticated || fallbackToken === keycloak.token) {
    return undefined;
  }

  return fallbackToken;
};

export const getValidFloorplannerKeycloakAccessToken = async (minValidity = KEYCLOAK_ACCESS_TOKEN_MIN_VALIDITY_SECONDS): Promise<string | undefined> => {
  const accessToken = await getValidKeycloakAccessToken(minValidity);

  if (!accessToken || !hasRequiredFloorplannerModule(keycloak.tokenParsed)) {
    return undefined;
  }

  return accessToken;
};

export default keycloak;
